import React, {useContext, useEffect, useRef, useState} from 'react';
import Card from "../../../components/card";
import TemplateDefault from "../../../components/template-default";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import SelectComponent from "../../../components/select-component";
import Button from "../../../components/button";
import {FaExternalLinkAlt} from "react-icons/fa";
import useFetchData from "../hooks/use-fetch-data";
import OrdersTable from "../components/orders-table";
import AttendancesTable from "../components/attendances-table";
import AttachesTable from "../../../components/attaches-table";
import InputDateTime from "../../../components/input-datetime";
import moment from "moment";
import AlertCard from "../../../components/alert-card";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {ProjectContext} from "../../../context/project-context";
import SshService from "../../../services/ssh-service";
import NocModal from "../components/noc-modal";
import hardwareAttendancesService from "../../../services/hardware-attendances-service";
import SshModal from "../../../components/ssh-modal";
import {isMobile} from 'react-device-detect';
import {FormProvider, useForm} from "react-hook-form";
import TextCorrectorComponent from "../../../components/text-corrector-component";
import FormField from "../../../components/react-hook-form/form-field";
import inputText from "../../../components/input-text";

const BASENAME = process.env.PUBLIC_URL;

const MySwal = withReactContent(Swal);

let statusOptions = [];

const HardwareAttendanceForm = () => {
    const initialState = {
        date_initial: new Date(),
        date_final: new Date(),
        status: {value: "Aberto", label: "Aberto"},
        project: null,
        requester: null,
        product: null,
        problem: "",
        observation: "",
        attachs: [],
        orders: [],
        title: ""
    }

    const {id} = useParams();
    const [searchParams] = useSearchParams();

    const {user} = useContext(ProjectContext);
    const hasSupervisorPermission = user.permissions.includes("supervisor");
    const onlySeeAttendances = user.permissions.includes("see_attendances");
    const canAlterStatus = user.permissions.includes("alter_status");

    const [state, setState] = useState(initialState);
    const [alertMessage, setAlertMessage] = useState("");
    const [resaleOnly, setResaleOnly] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [nocModal, setNocModal] = useState(false);
    const [sshModal, setSshModal] = useState(false);

    useEffect(() => {
        if (hasSupervisorPermission){
            statusOptions = [
                {value: "Aberto", label: "Aberto"},
                {value: "Encerrado", label: "Encerrado"},
                {value: "Realizado", label: "Serviço realizado"}
            ];
        }else{
            statusOptions = [
                {value: "Aberto", label: "Aberto"},
                {value: "Realizado", label: "Serviço realizado"}
            ];
        }
    }, [hasSupervisorPermission]);

    useEffect(() => {
        if (state.project) {
            document.title = `SI-${state.project.label}#${id ?? ""}`;
        } else {
            document.title = "SISTEMA INTERNO";
        }
    }, [id, state.project]);

    const attachesRef = useRef();

    const navigate = useNavigate();

    const {isError, isFetching, refetch} = useQuery({
        queryFn: () => hardwareAttendancesService.get(id).then((data) => {
            if (data.date_initial) data.date_initial = new Date(data.date_initial);

            const {user_creation_login, user_update_login, orders} = data

            const created_at = moment(data.created_at).format("DD/MM/YYYY HH:mm");
            const updated_at = moment(data.updated_at).format("DD/MM/YYYY HH:mm");

            const title = `Cadastrado em ${created_at} por ${user_creation_login} - Última atualização em ${updated_at} por ${user_update_login}`;

            let status

            if (data.status.toUpperCase() === "ENCERRADO") {
                status = {value: "Encerrado", label: "Encerrado"}
            }else {
                status = statusOptions.find(status => status.value.toUpperCase() === data.status.toUpperCase())
            }
            setState({
                date_initial: data.date_initial,
                date_final: data.date_final ? new Date(data.date_final) : new Date(),
                status: status,
                project: data?.projectSelected ?? null,
                requester: data?.requesterSelected ?? null,
                product: data?.productSelected ?? null,
                problem: data.problem,
                observation: data.observation,
                attachs: data.attachs,
                title,
                orders
            });

            const projectAlerts = handleProjectAlerts(data?.projectSelected);
            setAlertMessage(projectAlerts.message);
            setResaleOnly(projectAlerts.resaleOnly);

            return data;
        }),
        queryKey: ['hardware-attendances', {id}],
        enabled: !!id,
        retry: 0
    });

    if (isError) navigate("/404");

    const {projects, users, products} = useFetchData(state.project);

    useEffect(() => {
        if (!projects || !searchParams.get("ibge") || !searchParams.get("pcl")) {
            setIsLoading(false);
            return;
        }
        
        const project = projects.find(p => p.code === searchParams.get("ibge")) ?? null
        const product = products?.find(p => p.pcl_id === searchParams.get("pcl")) ?? null;
        const requester = {value: user.id, label: user.name };

        setIsLoading(false);
        setState(prev => ({...prev, project, product, requester}));
        const projectAlerts = handleProjectAlerts(project);
        setAlertMessage(projectAlerts.message);
        setResaleOnly(projectAlerts.resaleOnly);
    }, [projects, products, searchParams, user.id, user.name]);

    const handleSelect = (id, value) => {
        if (id === "project") {
            setState(prev => ({...prev, project: value, product: null, requester: null}));
            const projectAlerts = handleProjectAlerts(value);
            setAlertMessage(projectAlerts.message);
            setResaleOnly(projectAlerts.resaleOnly);
            return;
        }

        setState(prev => ({...prev, [id]: value}));
    };

    const handleDate = (value, item) => setState(prev => ({...prev, [item]: value}));

    const handleProjectAlerts = (project) => {
        if (!project) return {message: "", resaleOnly: false};

        const {expiration_license, expiration_support, resale_only} = project;

        const now = moment(new Date());
        const durationSupport = moment.duration(now.diff(expiration_support));
        const durationLicense = moment.duration(now.diff(expiration_license));
        const daysSupport = Math.round(durationSupport.asDays() * (-1));
        const daysLicense = Math.round(durationLicense.asDays() * (-1));

        let message = "";
        if (daysSupport <= 0) message = 'Prazo de atendimento expirado para este projeto.';

        if (daysLicense <= 18 && daysLicense > 0) {
            message = `Licença vence em ${daysLicense} dias.`;
        } else if (daysLicense <= 0) {
            message = "Licença vencida.";
        }

        return {message, resaleOnly: !!resale_only};
    }

    useEffect(() => {
        if ((!alertMessage && !resaleOnly) || onlySeeAttendances) return;

        MySwal.fire({
            icon: "warning",
            title: "Aviso!",
            html: [
                "<p>Não podemos atender.</p>",
                alertMessage ? `<p>${alertMessage}</p>` : "",
                resaleOnly ? "<p>Esse projeto deve ser atendido apenas pela revenda vinculada.</p>" : "",
            ].join(""),
        });
    }, [alertMessage, resaleOnly, onlySeeAttendances]);

    const submit = () => {

        const dataRaw = methods.getValues()

        const formData = new FormData();

        const form = {
            date_initial: state.date_initial,
            date_final: state.date_final,
            status: state.status?.value,
            project_id: state.project?.value,
            product_id: state.product?.value,
            requester_id: state.requester?.value,
            problem: dataRaw?.problem,
            observation: dataRaw?.observation,
        }

        Object.keys(form).filter(key => form[key]).forEach(key => {
            if (key.includes("date")) form[key] = form[key].toISOString();

            formData.append(key, form[key])
        });

        const attachesRow = [];
        attachesRef.current?.api.forEachNode((node) => {
            attachesRow.push(node.data);

            if (node.data?.file) formData.append("attaches[]", node.data.file);
        });

        formData.append("attachs", JSON.stringify(attachesRow));

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? hardwareAttendancesService.edit(formData, id) : hardwareAttendancesService.create(formData);

        response
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                id ? refetch() : navigate(`/hardware-attendances/form/${data.id}`);
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            });
    }

    const destroy = () => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir o chamado?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                hardwareAttendancesService.delete(id)
                    .then(() => {
                        navigate(`/hardware-attendances`);
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const openSsh = () => {
        if (!state.project?.value) {
            toast.error(AlertMessage.invalidSsh);
            return;
        }

        if (state.project.ssh.length > 1) {
            setSshModal(true);
            return;
        }

        const ssh = state.project.ssh;
        SshService.openSshConnection(state.project.value, ssh[0]?.id);
    }

    const openNewProduct = () => {
        window.open(`${BASENAME}/products?ibge=${state.project.code}`, `products-ibge=${state.project.code}`);
    }

    const openIpRelation = () => {
        if (!state.product || !state.project || !state.product?.pcl_id) return;

        setNocModal(true);
    }

    const openMap = () => {
        const { product: {lat, lng, place} } = state;

        window.open(`geo:${lat},${lng}?q=${lat},${lng}(${place})`, "_blank");
    }

    const methods = useForm({
        values: state
    });

    return (
        <TemplateDefault title={"Cadastro de atendimento" + (id ? ` #${id}` : "")} isLoading={isLoading || isFetching}>
            <Card title={state.title} headerClass="text-center">
                {
                    (isMobile && state?.product?.lng && state?.product?.lat) &&
                    <Button col={"mb-4"} target="primary" onClick={() => openMap()}>
                        Ver no mapa
                    </Button>
                }
                <div className="lg:grid xl:grid-cols-3 gap-2">
                    <div className="grid lg:grid-cols-12 gap-4 col-span-2">
                        <div className="col-span-12 lg:col-span-4">
                            <InputDateTime id="date_initial" label="Abertura" disabled={onlySeeAttendances}
                                           onChange={(date) => handleDate(date, "date_initial")} selected={state.date_initial} showTime/>
                        </div>

                        {
                            state.status?.value === "Encerrado" &&
                            <div className="col-span-12 lg:col-span-4">
                                <InputDateTime id="date_final" label="Encerramento" disabled={onlySeeAttendances || !canAlterStatus}
                                               onChange={(date) => handleDate(date, "date_final")} selected={state.date_final} showTime/>
                            </div>
                        }

                        <div className="col-span-12 lg:col-span-4 text-gray-900">
                            <SelectComponent
                                instanceId="status"
                                label="Status"
                                options={statusOptions}
                                value={state.status}
                                onChange={(value) => handleSelect("status", value)}
                                name="status"
                                isClearable={false}
                                isDisabled={(id && !canAlterStatus)}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6 text-gray-900">
                            <SelectComponent
                                instanceId="project"
                                label="Projeto"
                                options={projects}
                                value={state.project}
                                onChange={(value) => handleSelect("project", value)}
                                isDisabled={onlySeeAttendances}
                                name="project"
                            />
                        </div>

                        {
                            (user.permissions.includes("dashboard") || !onlySeeAttendances) &&
                                <Button target={"primary"} disabled={!state.project?.value}
                                        title={"Iniciar conexão SSH"} col="col-span-2"
                                        onClick={openSsh}>
                                    <FaExternalLinkAlt/>
                                </Button>
                        }

                        <div className="col-span-10 lg:col-span-4 text-gray-900">
                            <SelectComponent
                                instanceId="requester"
                                label="Solicitante"
                                options={users}
                                value={state.requester}
                                onChange={(value) => handleSelect("requester", value)}
                                name="requester"
                                isDisabled={!state.project || onlySeeAttendances}
                            />
                        </div>

                        <div className="col-span-12 2xl:col-span-6 text-gray-900">
                            <SelectComponent
                                instanceId="product"
                                label="Produto"
                                options={products}
                                value={state.product}
                                onChange={(value) => handleSelect("product", value)}
                                name="product"
                                isDisabled={!state.project || onlySeeAttendances}
                            />
                        </div>

                        {
                            user.permissions.includes("products") &&
                                <Button col="col-span-6 2xl:col-span-3" target="primary" disabled={!state.project} onClick={openNewProduct}>
                                    Novo produto
                                </Button>
                        }

                        {
                            user.permissions.includes("noc") &&
                                <Button col="col-span-6 2xl:col-span-3" target="primary" disabled={!state.product} onClick={openIpRelation}>
                                    Relação de IPs
                                </Button>
                        }
                    </div>



                    <div className="lg:row-span-3 mt-4 lg:mt-0 flex flex-col gap-4 col-span-1">
                        {
                            (!!alertMessage || resaleOnly) && !onlySeeAttendances &&
                            <AlertCard title="Aviso" type={"danger"}>
                                <p>Não podemos atender.</p>
                                {alertMessage && <p>{alertMessage}</p>}
                                {resaleOnly && <p>Esse projeto deve ser atendido apenas pela revenda vinculada.</p>}
                            </AlertCard>
                        }

                        {
                            state.requester && !onlySeeAttendances &&
                            <AlertCard title="Informações" type={"info"}>
                                <p><b>Solicitante:</b> {state.requester?.phone ? state.requester.phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3") :  ""}</p>
                                <p><b>Projeto:</b> {state.project?.phone ? state.project.phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3") :  ""}</p>
                                <p><b>E-mail:</b> {state.requester?.email ?? ""}</p>
                            </AlertCard>
                        }
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-12 mt-4">
                    <FormProvider {...methods}>
                        <div className={"col-span-6 space-y-4"}>
                            <TextCorrectorComponent setIsLoading={setIsLoading} setState={setState} name={"problem"} module={"hardware_attendances"} url={"/api/si/infrastructure/text-correction"} disable={onlySeeAttendances}>
                                <FormField
                                    as={inputText}
                                    name={"problem"}
                                    label={"Requisição"}
                                    textAreaClass={"h-40"}
                                    disabled={onlySeeAttendances}
                                    onChange={(e) => {
                                        const valor = e?.target?.value !== undefined ? e.target.value : e;

                                        setState(prev => ({ ...prev, problem: valor }));
                                    }}
                                />
                            </TextCorrectorComponent>
                        </div>
                        <div className={"col-span-6 space-y-4"}>
                            <TextCorrectorComponent setIsLoading={setIsLoading} setState={setState} name={"observation"} module={"hardware_attendances"} url={"/api/si/infrastructure/text-correction"} disable={onlySeeAttendances}>
                                <FormField
                                    as={inputText}
                                    name={"observation"}
                                    label={"Observação"}
                                    textAreaClass={"h-40"}
                                    disabled={onlySeeAttendances}
                                    onChange={(e) => {
                                        const valor = e?.target?.value !== undefined ? e.target.value : e;

                                        setState(prev => ({ ...prev, observation: valor }));
                                    }}
                                />
                            </TextCorrectorComponent>
                        </div>
                    </FormProvider>
                </div>
            </Card>

            {id && <OrdersTable orders={state.orders} id={id} onlySeeAttendances={onlySeeAttendances}/>}

            <AttendancesTable project={state.project} setIsLoading={setIsLoading} onlySeeAttendances={onlySeeAttendances}/>

            <AttachesTable attaches={state.attachs} gridRef={attachesRef} onlySeeAttendances={onlySeeAttendances} submit={submit}/>

            {
                !onlySeeAttendances &&
                <Card wrapperClass="mt-5">
                    <div className="flex justify-end gap-3">
                        {
                            id &&
                            <Button target="danger" onClick={() => destroy()}>
                                Excluir
                            </Button>
                        }
                        <Button target="warning" onClick={() => navigate("/hardware-attendances")}>
                            Cancelar
                        </Button>
                        <Button target="success" onClick={() => submit()}>
                            Salvar
                        </Button>
                    </div>
                </Card>
            }

            {
                onlySeeAttendances &&
                <Card wrapperClass="mt-5">
                    <div className="flex justify-end gap-3">
                        <Button target="warning" onClick={() => navigate("/hardware-attendances")}>
                            Voltar
                        </Button>
                    </div>
                </Card>
            }

            <NocModal open={nocModal} set={setNocModal} pcl_id={state.product?.pcl_id} ibge={state.project?.code}/>

            <SshModal open={sshModal} set={setSshModal} project={state.project}/>
        </TemplateDefault>
    );
};

export default HardwareAttendanceForm;
